import React, { useMemo, useState } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { Layers, Box, ChevronRight, ChevronDown } from 'lucide-react';
import { BomItem } from '../types';
import { NextStepButton, StepProgressIndicator } from '../components/WorkflowNavigation';

export default function StructureTreePage() {
    const { allData } = useAppContext();
    const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());

    const toggleExpand = (id: number) => {
        setExpandedNodes(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const buildTree = (parentId: number | null): BomItem[] => {
        return allData.bomItems.filter(item =>
            parentId === null ? !item.parentId : item.parentId === parentId
        );
    };

    const renderNode = (item: BomItem, depth: number) => {
        const children = buildTree(item.id);
        const hasChildren = children.length > 0;
        const isExpanded = expandedNodes.has(item.id);

        return (
            <div key={item.id} className="select-none">
                <div
                    className={`flex items-center py-2 px-3 hover:bg-background/80 rounded transition-colors group ${depth === 0 ? 'bg-card mb-2 border border-border shadow-sm' : ''}`}
                    style={{ marginLeft: depth > 0 ? `${depth * 1.5}rem` : '0', borderLeft: depth > 0 ? '2px solid var(--border)' : 'none' }}
                >
                    <button
                        onClick={() => hasChildren && toggleExpand(item.id)}
                        className={`w-6 flex justify-center text-text-muted ${hasChildren ? 'hover:text-primary cursor-pointer' : 'cursor-default opacity-50'}`}
                    >
                        {hasChildren ? (isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />) : <Box size={14} />}
                    </button>
                    <div className="flex-1 flex items-center gap-2 ml-1">
                        <span className="font-semibold text-text-primary">{item.component}</span>
                        {item.partNumber && <span className="text-xs px-2 py-0.5 bg-background rounded-full text-text-secondary font-mono border border-border hidden sm:inline-block">{item.partNumber}</span>}
                    </div>
                </div>
                {hasChildren && isExpanded && (
                    <div className="pl-4">
                        {children.map(child => renderNode(child, depth + 1))}
                    </div>
                )}
            </div>
        );
    };

    const rootItems = buildTree(null);

    return (
        <div className="p-6 h-full flex flex-col bg-background/50">
            <div className="mb-6 flex items-center justify-between border-b border-border pb-4">
                <div className="flex items-center gap-3">
                    <div className="bg-primary/10 p-2 rounded-lg text-primary">
                        <Layers size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold font-heading text-text-primary">Structure Analysis</h1>
                        <p className="text-text-secondary mt-1">AIAG-VDA Step 2: Boundary Definition & System Structure</p>
                    </div>
                </div>
                <div className="hidden lg:block">
                    <StepProgressIndicator currentStep={2} completedSteps={[1]} />
                </div>
            </div>

            <div className="flex-1 bg-card rounded-xl border border-border shadow-sm overflow-auto p-4 max-h-[calc(100vh-200px)]">
                {rootItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-text-secondary p-8 bg-background/50 rounded-lg border border-dashed border-border/50">
                        <Box size={40} className="mb-4 opacity-30" />
                        <p className="font-medium text-lg">No Structure Declared</p>
                        <p className="mt-1 text-sm text-center max-w-sm">Use the System Definition module to generate your base bill of materials (BOM) before analyzing structure.</p>
                    </div>
                ) : (
                    <div className="space-y-1">
                        {rootItems.map(root => renderNode(root, 0))}
                    </div>
                )}
            </div>

            {/* Continue to Next Step */}
            <div className={`p-4 mt-4 rounded-lg border flex items-center justify-between bg-card border-border`}>
                <div>
                    <h3 className="font-semibold text-text-primary">Ready to continue?</h3>
                    <p className="text-sm text-text-secondary">Proceed to analyze functions and requirements</p>
                </div>
                <NextStepButton currentStep={2} isComplete={rootItems.length > 0} />
            </div>
        </div>
    );
}
